import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../../shared/api/client';
import { Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { bibleLink } from './BiblePage';

export interface AtlasPerson { id: string; name: string; qualifier: string | null; en: string; gender: string | null; verseCount: number; firstRef: string | null }
export interface AtlasPlace { id: string; name: string; en: string; type: string | null; lat: number | null; lon: number | null; verseCount: number }
export interface AtlasEvent { id: number; title: string; en: string; year: number | null; duration: string | null; partOf: number | null }
type Verse = { ref: string; text: string | null };
type PersonDetail = AtlasPerson & {
  rv: string | null; birthYear: number | null; deathYear: number | null; groups: string[];
  father: AtlasPerson[]; mother: AtlasPerson[]; siblings: AtlasPerson[]; children: AtlasPerson[]; partners: AtlasPerson[];
  birthPlace: { id: string; name: string }[]; deathPlace: { id: string; name: string }[]; events: AtlasEvent[]; verses: Verse[]; allRefs: string[]; attribution: string;
};
type PlaceDetail = AtlasPlace & { rv: string | null; people: (AtlasPerson & { born: boolean; died: boolean })[]; events: AtlasEvent[]; verses: Verse[]; allRefs: string[]; attribution: string };
type EventDetail = Omit<AtlasEvent, 'partOf'> & { partOf: AtlasEvent | null; participants: AtlasPerson[]; places: AtlasPlace[]; verses: Verse[]; allRefs: string[]; attribution: string };

/** Año aproximado: -1000 → «1000 a. C.». */
export const yearLabel = (y: number | null) => (y === null ? '' : y < 0 ? `${-y} a. C.` : `${y} d. C.`);
const DURATION: Record<string, [string, string]> = { Y: ['año', 'años'], D: ['día', 'días'], M: ['mes', 'meses'], W: ['semana', 'semanas'] };
const durationLabel = (d: string | null) => {
  const m = d?.match(/^(\d+)([YDMW])$/);
  if (!m) return '';
  const n = Number(m[1]);
  return `${n} ${DURATION[m[2]!]![n === 1 ? 0 : 1]}`;
};
const personLabel = (p: AtlasPerson) => (p.qualifier ? `${p.name} (${p.qualifier})` : p.name);

function Attribution({ text }: { text?: string }) {
  return (
    <p className="small muted atlas__credit">
      {text ??
        'Personajes, lugares y cronología: Theographic Bible Metadata (Robert Rouse), licencia CC BY-SA 4.0. Traducción y adaptación al español: KEORA, bajo la misma licencia. Fechas aproximadas.'}{' '}
      <a href="https://github.com/robertrouse/theographic-bible-metadata" target="_blank" rel="noopener noreferrer">
        Fuente
      </a>{' '}
      ·{' '}
      <a href="https://creativecommons.org/licenses/by-sa/4.0/deed.es" target="_blank" rel="noopener noreferrer">
        Licencia
      </a>
    </p>
  );
}

type BibleTab = 'leer' | 'estudiar' | 'minutos' | 'memorizar' | 'trivia' | 'temas' | 'diccionario' | 'historia' | 'personajes' | 'mapa' | 'cronologia';

/** Opciones de «Estudiar»: aparecen como tarjetas en /app/biblia/estudiar y como sub-pestañas dentro de cada una. */
export const STUDY_SECTIONS = [
  ['temas', '/app/biblia/temas', 'Temas', 'Qué dice la Biblia sobre un tema.'],
  ['diccionario', '/app/biblia/diccionario', 'Diccionario', 'Palabras, nombres y costumbres de la Biblia.'],
  ['historia', '/app/biblia/historia', 'Historia', 'Cómo llegó la Biblia hasta nosotros.'],
  ['personajes', '/app/biblia/personajes', 'Personajes', 'Las personas de la Biblia y dónde aparecen.'],
  ['mapa', '/app/biblia/mapa', 'Mapa', 'Los lugares de la Biblia en el mapa.'],
  ['cronologia', '/app/biblia/cronologia', 'Cronología', 'Los hechos de la Biblia en orden.'],
] as const;

/** Pestañas de la Biblia en cinco grupos: Leer · Estudiar · En minutos · Memorizar · Jugar. Estudiar tiene su propia sub-navegación. */
export function BibleTabs({ active }: { active: BibleTab }) {
  const studying = active === 'estudiar' || STUDY_SECTIONS.some(([k]) => k === active);
  const group = studying ? 'estudiar' : active === 'trivia' ? 'jugar' : active;
  const tabs = [
    ['leer', '/app/biblia', 'Leer'],
    ['estudiar', '/app/biblia/estudiar', 'Estudiar'],
    ['minutos', '/app/biblia/minutos', 'En minutos'],
    ['memorizar', '/app/biblia/memorizar', 'Memorizar'],
    ['jugar', '/app/biblia/trivia', 'Jugar'],
  ] as const;
  return (
    <>
      <nav className="tabs tabs--scroll" aria-label="Biblia">
        {tabs.map(([key, to, label]) => (
          <Link key={key} to={to} className={`tabs__tab${group === key ? ' tabs__tab--active' : ''}`} aria-current={group === key ? 'page' : undefined}>
            {label}
          </Link>
        ))}
      </nav>
      {studying && active !== 'estudiar' ? (
        <nav className="tabs tabs--scroll tabs--sub" aria-label="Estudiar">
          {STUDY_SECTIONS.map(([key, to, label]) => (
            <Link key={key} to={to} className={`tabs__tab${active === key ? ' tabs__tab--active' : ''}`} aria-current={active === key ? 'page' : undefined}>
              {label}
            </Link>
          ))}
        </nav>
      ) : null}
    </>
  );
}

/** Estudiar: las seis ayudas de estudio como tarjetas. */
export function BibleStudyHubPage() {
  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="estudiar" />
      <ul className="hub">
        {STUDY_SECTIONS.map(([key, to, label, hint]) => (
          <li key={key}>
            <Link to={to} className="hub__item">
              <span>
                <strong>{label}</strong>
                <span className="small muted">{hint}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Verses({ verses, allRefs }: { verses: Verse[]; allRefs: string[] }) {
  const [all, setAll] = useState(false);
  return (
    <Card title={`Citas (${allRefs.length})`}>
      <ul className="list">
        {verses.map((v) => (
          <li key={v.ref} className="list__item">
            <Link to={bibleLink(v.ref)}>
              <strong>{v.ref}</strong>
            </Link>
            {v.text ? <span className="block small">{v.text}</span> : null}
          </li>
        ))}
      </ul>
      {allRefs.length > verses.length ? (
        all ? (
          <p className="small atlas__refs">
            {allRefs.slice(verses.length).map((r, i) => (
              <span key={r}>
                {i ? ' · ' : ''}
                <Link to={bibleLink(r)}>{r}</Link>
              </span>
            ))}
          </p>
        ) : (
          <button type="button" className="btn btn--ghost" onClick={() => setAll(true)}>
            Ver las {allRefs.length - verses.length} citas restantes
          </button>
        )
      ) : null}
    </Card>
  );
}

function PeopleLinks({ label, people }: { label: string; people: AtlasPerson[] }) {
  if (people.length === 0) return null;
  return (
    <>
      <dt>{label}</dt>
      <dd>
        {people.map((p, i) => (
          <span key={p.id}>
            {i ? ', ' : ''}
            <Link to={`/app/biblia/personajes/${p.id}`}>{personLabel(p)}</Link>
          </span>
        ))}
      </dd>
    </>
  );
}

// ── Personajes ──────────────────────────────────────────────────────────────

export function PeoplePage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const [text, setText] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (text !== q) setParams(text ? { q: text } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  const list = useAsync((signal) => api.get<{ items: AtlasPerson[]; attribution: string }>(`/bible/atlas/people${q ? `?q=${encodeURIComponent(q)}` : ''}`, signal), [q]);
  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="personajes" />
      <Card>
        <Field label="Buscar un personaje" name="q" value={text} onChange={(e) => setText(e.target.value)} placeholder="David, María, Ezequías…" autoComplete="off" />
      </Card>
      {list.status === 'loading' ? <Loading /> : null}
      {list.status === 'error' ? <ErrorState error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'ready' ? (
        list.data.items.length === 0 ? (
          <EmptyState title="No encontramos ese personaje." />
        ) : (
          <Card title={q ? 'Resultados' : 'Personajes más mencionados'}>
            <ul className="list">
              {list.data.items.map((p) => (
                <li key={p.id} className="list__item">
                  <Link to={`/app/biblia/personajes/${p.id}`}>
                    <strong>{personLabel(p)}</strong>
                  </Link>
                  <span className="muted small">
                    {' '}
                    · {p.verseCount} {p.verseCount === 1 ? 'versículo' : 'versículos'}
                    {p.firstRef ? ` · desde ${p.firstRef}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )
      ) : null}
      <Attribution />
    </div>
  );
}

export function PersonPage() {
  const { personId = '' } = useParams();
  const data = useAsync((signal) => api.get<PersonDetail>(`/bible/atlas/people/${personId}`, signal), [personId]);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const p = data.data;
  const years = p.birthYear !== null || p.deathYear !== null ? [yearLabel(p.birthYear), yearLabel(p.deathYear)].join(' – ') : null;
  return (
    <div className="page page--narrow bible-wide">
      <p className="small">
        <Link to="/app/biblia/personajes">← Personajes</Link>
      </p>
      <header className="page__header">
        <h1>{p.name}</h1>
        <p className="muted">
          {p.qualifier ? `${p.qualifier} · ` : ''}
          {p.gender === 'F' ? 'Mujer' : p.gender === 'M' ? 'Hombre' : ''}
          {years ? ` · ${years} (aprox.)` : ''} · Mencionado en {p.verseCount} {p.verseCount === 1 ? 'versículo' : 'versículos'}
        </p>
      </header>
      <Card title="Familia y datos">
        <dl className="details">
          <PeopleLinks label="Padre" people={p.father} />
          <PeopleLinks label="Madre" people={p.mother} />
          <PeopleLinks label={p.partners.length > 1 ? 'Cónyuges' : 'Cónyuge'} people={p.partners} />
          <PeopleLinks label="Hermanos" people={p.siblings} />
          <PeopleLinks label="Hijos" people={p.children} />
          {p.birthPlace.length ? (
            <>
              <dt>Nació en</dt>
              <dd>{p.birthPlace.map((x) => <Link key={x.id} to={`/app/biblia/lugares/${x.id}`}>{x.name}</Link>)}</dd>
            </>
          ) : null}
          {p.deathPlace.length ? (
            <>
              <dt>Murió en</dt>
              <dd>{p.deathPlace.map((x) => <Link key={x.id} to={`/app/biblia/lugares/${x.id}`}>{x.name}</Link>)}</dd>
            </>
          ) : null}
          {p.groups.length ? (
            <>
              <dt>Grupo</dt>
              <dd>{p.groups.join(', ')}</dd>
            </>
          ) : null}
          <dt>Otros nombres</dt>
          <dd className="muted">{[p.rv ? `${p.rv} (RV 1909)` : null, `${p.en} (inglés)`].filter(Boolean).join(' · ')}</dd>
        </dl>
      </Card>
      {p.events.length ? (
        <Card title="En la cronología">
          <ul className="list">
            {p.events.map((e) => (
              <li key={e.id} className="list__item">
                <Link to={`/app/biblia/cronologia/${e.id}`}>{e.title}</Link>
                <span className="muted small"> · {yearLabel(e.year)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      <Verses verses={p.verses} allRefs={p.allRefs} />
      <Attribution text={p.attribution} />
    </div>
  );
}

// ── Mapa ─────────────────────────────────────────────────────────────────────

/** Mapa con Leaflet y OpenStreetMap; se carga solo al abrirlo. */
function PlacesMap({ places, focus, height = '60vh', onOpen }: { places: AtlasPlace[]; focus: AtlasPlace | null; height?: string; onOpen?: (id: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<import('leaflet').Map | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let disposed = false;
    void (async () => {
      try {
        const L = (await import('leaflet')).default;
        await import('leaflet/dist/leaflet.css');
        if (disposed || !box.current) return;
        const m = L.map(box.current, { scrollWheelZoom: true }).setView([31.8, 35.2], 6);
        map.current = m;
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 18,
          attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
        }).addTo(m);
        for (const p of places) {
          if (p.lat === null || p.lon === null) continue;
          const r = Math.min(10, 3 + Math.log2(1 + p.verseCount));
          const mk = L.circleMarker([p.lat, p.lon], { radius: r, color: '#1f3440', weight: 1, fillColor: p.id === focus?.id ? '#d97706' : '#2f6f8f', fillOpacity: 0.8 }).addTo(m);
          const el = document.createElement('div');
          const strong = document.createElement('strong');
          strong.textContent = p.name;
          el.append(strong, document.createElement('br'), `${p.type ?? 'Lugar'} · ${p.verseCount} ${p.verseCount === 1 ? 'versículo' : 'versículos'}`);
          if (onOpen) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'btn btn--ghost atlas__popup-btn';
            b.textContent = 'Ver lugar';
            b.onclick = () => onOpen(p.id);
            el.append(document.createElement('br'), b);
          }
          mk.bindPopup(el);
          if (p.id === focus?.id) mk.openPopup();
        }
        if (focus?.lat != null && focus.lon != null) m.setView([focus.lat, focus.lon], 9);
      } catch {
        if (!disposed) setError(true);
      }
    })();
    return () => {
      disposed = true;
      map.current?.remove();
      map.current = null;
    };
  }, [places, focus?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <p className="muted">No se pudo cargar el mapa.</p>;
  return <div ref={box} className="atlas__map" style={{ height }} role="region" aria-label="Mapa bíblico" />;
}

export function MapPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const data = useAsync((signal) => api.get<{ items: AtlasPlace[]; attribution: string }>('/bible/atlas/places', signal), []);
  const [q, setQ] = useState('');
  const focusId = params.get('lugar');
  const places = data.status === 'ready' ? data.data.items : [];
  const focus = places.find((p) => p.id === focusId) ?? null;
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const matches = useMemo(() => (q.trim().length >= 2 ? places.filter((p) => norm(`${p.name} ${p.en}`).includes(norm(q.trim()))).slice(0, 12) : []), [q, places]);
  return (
    <div className="page">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="mapa" />
      <Card>
        <Field label="Buscar un lugar" name="lugar" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jerusalén, Belén, mar de Galilea…" autoComplete="off" />
        {matches.length ? (
          <ul className="chips">
            {matches.map((p) => (
              <li key={p.id}>
                <button type="button" className="chip" onClick={() => (setParams({ lugar: p.id }), setQ(''))} disabled={p.lat === null}>
                  {p.name}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? <PlacesMap places={places} focus={focus} onOpen={(id) => navigate(`/app/biblia/lugares/${id}`)} /> : null}
      <Attribution />
    </div>
  );
}

export function PlacePage() {
  const { placeId = '' } = useParams();
  const data = useAsync((signal) => api.get<PlaceDetail>(`/bible/atlas/places/${placeId}`, signal), [placeId]);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const p = data.data;
  return (
    <div className="page page--narrow bible-wide">
      <p className="small">
        <Link to={`/app/biblia/mapa?lugar=${p.id}`}>← Mapa</Link>
      </p>
      <header className="page__header">
        <h1>{p.name}</h1>
        <p className="muted">
          {p.type ?? 'Lugar'} · Mencionado en {p.verseCount} {p.verseCount === 1 ? 'versículo' : 'versículos'}
          {p.rv ? ` · RV 1909: ${p.rv}` : ''}
        </p>
      </header>
      {p.lat !== null ? <PlacesMap places={[p]} focus={p} height="16rem" /> : <p className="muted small">Ubicación desconocida.</p>}
      {p.people.length ? (
        <Card title="Personajes">
          <ul className="list">
            {p.people.map((x) => (
              <li key={x.id} className="list__item">
                <Link to={`/app/biblia/personajes/${x.id}`}>{personLabel(x)}</Link>
                <span className="muted small"> · {[x.born ? 'nació aquí' : null, x.died ? 'murió aquí' : null].filter(Boolean).join(' y ')}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      {p.events.length ? (
        <Card title="Lo que pasó aquí">
          <ul className="list">
            {p.events.map((e) => (
              <li key={e.id} className="list__item">
                <Link to={`/app/biblia/cronologia/${e.id}`}>{e.title}</Link>
                <span className="muted small"> · {yearLabel(e.year)}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
      <Verses verses={p.verses} allRefs={p.allRefs} />
      <Attribution text={`${p.attribution} Mapa: © colaboradores de OpenStreetMap.`} />
    </div>
  );
}

// ── Cronología ──────────────────────────────────────────────────────────────

export function TimelinePage() {
  const data = useAsync(
    (signal) => api.get<{ items: (AtlasEvent & { participants: { id: string; name: string }[] })[]; attribution: string }>('/bible/atlas/events', signal),
    [],
  );
  const [q, setQ] = useState('');
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const items = data.status === 'ready' ? data.data.items.filter((e) => !q.trim() || norm(`${e.title} ${e.participants.map((p) => p.name).join(' ')}`).includes(norm(q.trim()))) : [];
  // Agrupa por época para leerla como una línea del tiempo.
  const era = (y: number | null) =>
    y === null ? 'Sin fecha' : y < -2100 ? 'Los orígenes' : y < -1500 ? 'Los patriarcas' : y < -1050 ? 'Éxodo, conquista y jueces' : y < -930 ? 'El reino unido' : y < -586 ? 'Reinos de Israel y Judá' : y < -5 ? 'Exilio y regreso' : y < 34 ? 'Vida de Jesús' : 'La iglesia primitiva';
  const groups: { era: string; items: typeof items }[] = [];
  for (const e of items) {
    const g = era(e.year);
    if (groups.at(-1)?.era !== g) groups.push({ era: g, items: [] });
    groups.at(-1)!.items.push(e);
  }
  return (
    <div className="page page--narrow bible-wide">
      <header className="page__header">
        <h1>Biblia</h1>
      </header>
      <BibleTabs active="cronologia" />
      <Card>
        <Field label="Buscar en la cronología" name="q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Éxodo, David, Pablo…" autoComplete="off" />
      </Card>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {groups.map((g) => (
        <Card key={g.era} title={g.era}>
          <ol className="timeline">
            {g.items.map((e) => (
              <li key={e.id} className={`timeline__item${e.partOf ? ' timeline__item--sub' : ''}`}>
                <span className="timeline__year">{yearLabel(e.year)}</span>
                <Link to={`/app/biblia/cronologia/${e.id}`}>{e.title}</Link>
                {durationLabel(e.duration) && e.duration !== '1D' ? <span className="muted small"> · {durationLabel(e.duration)}</span> : null}
              </li>
            ))}
          </ol>
        </Card>
      ))}
      {data.status === 'ready' && items.length === 0 ? <EmptyState title="No encontramos eventos con ese texto." /> : null}
      <Attribution />
    </div>
  );
}

export function TimelineEventPage() {
  const { eventId = '' } = useParams();
  const data = useAsync((signal) => api.get<EventDetail>(`/bible/atlas/events/${eventId}`, signal), [eventId]);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <ErrorState error={data.error} onRetry={data.reload} />;
  const e = data.data;
  return (
    <div className="page page--narrow bible-wide">
      <p className="small">
        <Link to="/app/biblia/cronologia">← Cronología</Link>
      </p>
      <header className="page__header">
        <h1>{e.title}</h1>
        <p className="muted">
          {yearLabel(e.year)} (aprox.){durationLabel(e.duration) ? ` · Duración: ${durationLabel(e.duration)}` : ''}
          {e.partOf ? (
            <>
              {' '}
              · Parte de <Link to={`/app/biblia/cronologia/${e.partOf.id}`}>{e.partOf.title}</Link>
            </>
          ) : null}
        </p>
      </header>
      {e.participants.length || e.places.length ? (
        <Card title="Quiénes y dónde">
          <dl className="details">
            <PeopleLinks label="Personajes" people={e.participants} />
            {e.places.length ? (
              <>
                <dt>Lugares</dt>
                <dd>
                  {e.places.map((p, i) => (
                    <span key={p.id}>
                      {i ? ', ' : ''}
                      <Link to={`/app/biblia/lugares/${p.id}`}>{p.name}</Link>
                    </span>
                  ))}
                </dd>
              </>
            ) : null}
          </dl>
        </Card>
      ) : null}
      <Verses verses={e.verses} allRefs={e.allRefs} />
      <Attribution text={e.attribution} />
    </div>
  );
}

/** En la lectura: personajes y lugares del capítulo. */
export function ChapterAtlasCard({ book, chapter, action }: { book: string; chapter: number; action?: ReactNode }) {
  const data = useAsync((signal) => api.get<{ people: AtlasPerson[]; places: AtlasPlace[] }>(`/bible/atlas/chapter?book=${book}&chapter=${chapter}`, signal), [book, chapter]);
  if (data.status === 'loading') return <Card title="Personajes y lugares" action={action}><Loading /></Card>;
  if (data.status === 'error') return <Card title="Personajes y lugares" action={action}><ErrorState error={data.error} /></Card>;
  if (data.data.people.length === 0 && data.data.places.length === 0) return <Card title="Personajes y lugares" action={action}><p className="muted small">No hay personajes ni lugares registrados para este capítulo.</p></Card>;
  return (
    <Card title="Personajes y lugares" action={action}>
      {data.data.people.length ? (
        <p className="small">
          <strong>Personajes: </strong>
          {data.data.people.slice(0, 20).map((p, i) => (
            <span key={p.id}>
              {i ? ', ' : ''}
              <Link to={`/app/biblia/personajes/${p.id}`}>{p.name}</Link>
            </span>
          ))}
        </p>
      ) : null}
      {data.data.places.length ? (
        <p className="small">
          <strong>Lugares: </strong>
          {data.data.places.slice(0, 20).map((p, i) => (
            <span key={p.id}>
              {i ? ', ' : ''}
              <Link to={p.lat !== null ? `/app/biblia/mapa?lugar=${p.id}` : `/app/biblia/lugares/${p.id}`}>{p.name}</Link>
            </span>
          ))}
        </p>
      ) : null}
    </Card>
  );
}
